import {
  Component,
  computed,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Output,
  signal,
  viewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faAngleDoubleLeft, faAngleDoubleRight, faChartSimple, faXmark } from "@fortawesome/free-solid-svg-icons";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { PercentageValuePipe } from "../../../../core/pipes";
import { TranslateTokenPipe } from "../../../../core/pipes/translate-token-pipe";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import type { AggregationPerfModelType, SelectedPeriodModelType } from "../../../models";
import { LanguageService } from "../../../services";
import { CountUpComponent } from "../../count-up";
import { ScoresByAggregationTypeComponent } from "../../modals";

const DISABLE_KPI_LIST = [
  102576, 102779, 102668, 102781, 102667, 102581, 102641, 102639, 102640, 102560, 102778, 102669, 102780, 102670,
  102565, 102638, 102636, 102637, 102570, 102566, 102582, 102586,
];

const PROJECTS_AGGREGATION_TYPES = [
  104600, 104604, 104605, 104606, 104607, 104610, 104614, 104615, 104616, 104617, 104620, 104621, 104622, 104623,
  104625, 104626, 104627, 104628,
];

@Component({
  selector: "adaa-aggregation-perf-card",
  templateUrl: "./aggregation-perf-card.html",
  styleUrl: "./aggregation-perf-card.scss",
  imports: [
    FontAwesomeModule,
    TranslateTokenPipe,
    PercentageValuePipe,
    CountUpComponent,
    TranslatePipe,
    ScoresByAggregationTypeComponent,
  ],
})
export class AggregationPerfCardComponent {
  readonly languageService = inject(LanguageService);
  readonly modalService = inject(NgbModal);
  private _router = inject(Router);

  data = input.required<AggregationPerfModelType>();
  period = input<SelectedPeriodModelType>();
  hasWeightsProblem = input<
    | {
        value: boolean;
        message?: undefined;
      }
    | {
        value: boolean;
        message: string;
      }
  >();

  showMore = signal<boolean>(false);
  AdaaHelper = AdaaHelper;

  color = computed(() => Constants.COLOR_CODES[this.data().color]);
  amount = computed(() => this.data().value);
  image = computed(() => {
    if (!this.data()) return null;

    const imageId = this.data()?.id;

    return `/assets/images/aggregation_types/${imageId}.png`;
  });
  children = computed(() => {
    if (!this.data()?.childPerformances) {
      return [];
    }
    return this.data().childPerformances;
  });

  isColumnLayout = computed(() => this.children().length <= 2);
  cardColor = computed(() => (AdaaHelper.isDefined(this.amount()) ? this.color() : this.defaultColor));
  progress = computed(() => Math.max(0, Math.min(100, this.amount() ?? 0)));

  scoresByAggregationType = viewChild.required<ScoresByAggregationTypeComponent>("scoresByAggregationType");

  @Output() performanceGraphClicked = new EventEmitter<AggregationPerfModelType>();

  @HostBinding("class.is-open") public get isMoreOpen() {
    return this.showMore();
  }

  @HostBinding("class.show-in-row") public get isShowInRow() {
    return this.showMore() && !this.isColumnLayout();
  }

  readonly defaultColor = Constants.COLOR_CODES[Constants.CONSTANT_COLORS.EVALUATION_GRAY];
  readonly isDefined = AdaaHelper.isDefined;
  readonly graphIcon = faChartSimple;
  readonly closeIcon = faXmark;
  readonly moreIcon = {
    rtl: faAngleDoubleLeft,
    ltr: faAngleDoubleRight,
  };

  public getColor(code: number) {
    return Constants.COLOR_CODES[code];
  }

  public getChildColor(child: AggregationPerfModelType) {
    if (!AdaaHelper.isDefined(child.value)) return this.defaultColor;
    return this.getColor(child.color);
  }

  hideKpiListButton = (id: number) => !DISABLE_KPI_LIST.find((item) => item === id);

  public openUri(uri: string) {
    const uriObj = JSON.parse(uri);
    this._router.navigateByUrl(uriObj.uri);
  }

  public buttonClicked(event: Event, type: "kpi" | "graph") {
    event.stopPropagation();
    switch (type) {
      case "kpi": {
        return this.scoresByAggregationType().open();
      }

      case "graph": {
        return this.performanceGraphClicked.emit(this.data());
      }
    }
  }

  public isProjectScore(aggregationType: number) {
    return PROJECTS_AGGREGATION_TYPES.includes(aggregationType);
  }
}
